import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Install banner - surfaces the browser's "Add to home screen" prompt
 * (Chromium / Edge / Android). The `beforeinstallprompt` event fires only
 * when the browser deems the app installable (manifest + SW + visit count).
 *
 * On iOS Safari this event doesn't fire - there's no programmatic install
 * API there. iOS users add the PWA via Share → "Add to Home Screen" manually.
 *
 * The banner shows once per session; dismissing it sets a sessionStorage
 * flag so it doesn't reappear on the next page.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "ai-leasing-os.install-banner.dismissed";

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

    function onPrompt(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    }
    window.addEventListener("beforeinstallprompt", onPrompt as EventListener);

    // Hide if the user installs from the browser UI directly.
    function onInstalled() {
      setVisible(false);
      setDeferred(null);
    }
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt as EventListener);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setVisible(false);
    setDeferred(null);
  }

  if (!visible || !deferred) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40 rounded-lg border border-slate-200 bg-brand-surface shadow-lg p-3 flex items-start gap-3">
      <div className="h-9 w-9 shrink-0 rounded-md flex items-center justify-center text-white"
           style={{ background: "var(--brand-primary)" }}>
        <Download className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">Install on this device</div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Add the dashboard to your home screen - opens in its own window, no browser chrome.
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Button size="sm" onClick={install}>Install</Button>
          <Button size="sm" variant="ghost" onClick={dismiss}>Not now</Button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-slate-400 hover:text-slate-700 shrink-0"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
